import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { connect } from "react-redux";
import { Popup } from "devextreme-react/popup";
import { Button } from "devextreme-react/button";
import {
    DataGrid,
    Column,
    Editing,
    FilterRow,
    Paging,
    Pager,
    Sorting,
    Scrolling,
    LoadPanel,
    Selection,
    ColumnChooser,
    ColumnChooserSearch,
    Toolbar as GridToolbar,
    Item as GridToolbarItem,
} from "devextreme-react/data-grid";
import { createStore } from "devextreme-aspnet-data-nojquery";
import DataSource from "devextreme/data/data_source";
import notify from "devextreme/ui/notify";
import { alert } from "devextreme/ui/dialog";
import axios from "axios";
import routes from "../../../../../assets/apiRoutes.json";
import FormField from "../../../../../dynamicFormBuilder/FormField";
import { buildDxFilters } from "../../../../../common/Utils";
import { MESSAGES } from "../../../../../common/Messages";
import { SelectItemsSearchLayout } from "./SelectItemsLayout";

const emptyGuid = "00000000-0000-0000-0000-000000000000";
const allowedPageSizes = [10, 20, 50, 100];

/**
 * OM Sales Order "Select Items" popup — React replacement for the legacy Delphi OMD0027.
 * Filters (FormField) at the top, a server-side-paged grid of items with availability below.
 * The user types an Ordered quantity (and optionally overrides Unit Price) on one or more
 * rows, then "Add Selected Items" appends them as new detail lines to the current order
 * (lots auto-selected by the backend). Entered quantities survive paging because they live
 * in the grid's batch-edit `changes`.
 */
function SelectItemsPopup(props) {
    const { showPopup = {}, setShowPopup, onSuccess, setLoadPanelVisible, oidc } = props;

    const visible = showPopup.visible === true;
    const headerRowId = showPopup.headerRowId;
    const warehouseId = showPopup.warehouseId;
    // Pricing context taken from the already-loaded order header (passed in via showPopup) so the backend
    // doesn't re-read the header. Sent with OmTransOrderDetailDto field names; the backend resolves the price
    // date from stage + ship/transaction dates.
    const customerId = showPopup.customerId || "";
    const consigneeId = showPopup.consigneeId || "";
    const priceLevel = showPopup.priceLevel || "";
    const stage = showPopup.stage || "";
    const toIsoDate = (v) => {
        if (!v) return "";
        const d = new Date(v);
        return isNaN(d.getTime()) ? "" : d.toISOString();
    };
    const shipDate = toIsoDate(showPopup.shippingDate);
    const transactionDate = toIsoDate(showPopup.transactionDate);

    // Range/macro selection handed over by the Range dialog (CommonReport) on the Details tab. When one is
    // active the rows come from GetItemsForSelection, which appends these criteria to the item query's WHERE
    // clause in SQL — the point being that a site with far more items than can be browsed is narrowed by the
    // database, not by the grid. With no range the popup keeps calling GetItems exactly as before.
    const rangeFilters = Array.isArray(showPopup.rangeFilters) ? showPopup.rangeFilters : [];
    const hasRange = rangeFilters.length > 0;
    const macroName = showPopup.macroName || "";
    const rangeFiltersJson = useMemo(() => (hasRange ? JSON.stringify(rangeFilters) : null), [hasRange, rangeFilters]);

    const accessToken = oidc?.user?.access_token;
    const currentUser = oidc?.user;
    const DYNAMICFORMSETTING = oidc?.user?.profile?.DYNAMICFORMSETTING;

    const dataGridRef = useRef(null);
    const formRefs = useRef({});

    // Every item filter starts empty — the Warehouse filter is deliberately NOT defaulted to the detail
    // line's warehouse, so the picker shows items from all warehouses. The line's warehouse is still what
    // the created rows are stamped with (see WarehouseID below). Key must match SelectItemsLayout.
    const initialFilters = useMemo(() => ({ WarehouseName: null }), []);

    const [formFields, setformFields] = useState(initialFilters);
    const [resultData, setResultData] = useState(null);
    // Controlled batch-edit changes — the authoritative list of every row whose Ordered/Price/Disc % was edited.
    const [changes, setChanges] = useState([]);
    // Row selection + rows the user removed from the grid (client-side only — never deleted from the DB).
    const [selectedRowKeys, setSelectedRowKeys] = useState([]);
    const [removedKeys, setRemovedKeys] = useState([]);

    // Server-side store; the dynamic filters are applied on the DataSource (DevExtreme sends them as loadOptions.filter).
    const buildDataSource = useCallback(() => {
        const store = createStore({
            key: "RowId",
            loadUrl: `${window._env_.REACT_APP_APIS}/${hasRange ? routes.IM.GetItemsForSelection : routes.IM.GetItems}`,
            loadParams: {
                // OmTransOrderDetailDto field names — bound on the server as the pricing context.
                transactionHeaderId: headerRowId || "",
                identityMasterRowId: customerId,
                consigneeRowId: consigneeId,
                priceLevel,
                stage,
                shipDate,
                transactionDate,
                // ItemSelectionFilter — ignored by GetItems, so they are only sent when a range is active.
                ...(hasRange ? { RangeFilters: rangeFiltersJson, MacroName: macroName } : {}),
            },
            onBeforeSend(method, ajaxOptions) {
                ajaxOptions.headers = {
                    access_token: accessToken,
                    Authorization: `Bearer ${accessToken}`,
                };
            },
            // A range the query cannot apply (a field the item view does not carry) or one matching more rows
            // than the server will return comes back as a message the user can act on — surfaced here rather
            // than only in the grid's error row.
            errorHandler: (error) => {
                notify({ message: error?.message || MESSAGES.FETCH_ERROR, width: 400 }, "error", 6000);
            },
        });
        return new DataSource({ store });
    }, [accessToken, headerRowId, customerId, consigneeId, priceLevel, stage, shipDate, transactionDate, hasRange, rangeFiltersJson, macroName]);

    // Build the dynamic DevExtreme filter from the FormField values (same helper List.js uses) and apply it,
    // AND exclude any rows the user removed from the grid (client-side delete — these never hit the DB).
    const loadWithFilters = useCallback((filters, excluded = []) => {
        const ds = buildDataSource();
        const parts = [];
        const dxFilters = buildDxFilters(filters, SelectItemsSearchLayout);
        if (dxFilters && dxFilters.length) parts.push(dxFilters);
        if (excluded && excluded.length) {
            const conds = excluded.map((k) => ["RowId", "<>", k]);
            let exclusion = conds[0];
            if (conds.length > 1) {
                exclusion = [];
                conds.forEach((c, i) => {
                    if (i) exclusion.push("and");
                    exclusion.push(c);
                });
            }
            parts.push(exclusion);
        }
        const combined = parts.length === 2 ? [parts[0], "and", parts[1]] : parts[0] || null;
        if (combined) ds.filter(combined);
        setResultData(ds);
    }, [buildDataSource]);

    // Reset filters + reload whenever the popup is opened.
    useEffect(() => {
        if (!visible) return;
        setformFields(initialFilters);
        setChanges([]);
        setSelectedRowKeys([]);
        setRemovedKeys([]);
        loadWithFilters(initialFilters, []);
    }, [visible, initialFilters, loadWithFilters]);

    const handleFieldChange = useCallback(() => {}, []);
    const handleFormLoad = useCallback(() => {}, []);

    const handleSearch = useCallback(
        (e) => {
            e.event.stopPropagation();
            const grid = dataGridRef.current?.instance();
            if (grid) grid.cancelEditData(); // drop any unsaved quantities from the previous result set
            setChanges([]);
            setSelectedRowKeys([]);
            setRemovedKeys([]);
            loadWithFilters(formFields, []);
        },
        [formFields, loadWithFilters]
    );

    const handleReset = useCallback(
        (e) => {
            e.event.stopPropagation();
            const grid = dataGridRef.current?.instance();
            if (grid) grid.cancelEditData();
            setformFields(initialFilters);
            setChanges([]);
            setSelectedRowKeys([]);
            setRemovedKeys([]);
            loadWithFilters(initialFilters, []);
        },
        [initialFilters, loadWithFilters]
    );

    // Delete the selected rows from the grid only (client-side) — excluded from the view, never deleted from the DB.
    const handleDeleteRows = useCallback(() => {
        if (!selectedRowKeys.length) return;
        const keys = selectedRowKeys;
        const next = removedKeys.concat(keys.filter((k) => !removedKeys.includes(k)));
        setRemovedKeys(next);
        // Drop any entered quantities for the removed rows so they aren't sent on "Add Selected Items".
        setChanges((prev) => prev.filter((c) => !keys.includes(c.key)));
        setSelectedRowKeys([]);
        loadWithFilters(formFields, next);
    }, [selectedRowKeys, removedKeys, formFields, loadWithFilters]);

    const handleHiding = useCallback(() => {
        setShowPopup((prev) => ({ ...prev, visible: false }));
    }, [setShowPopup]);

    const handleAddSelected = useCallback(async () => {
        if (!headerRowId || headerRowId === emptyGuid) {
            alert("Save the order before selecting items.", "Select Items");
            return;
        }
        const grid = dataGridRef.current?.instance();
        if (grid) grid.closeEditCell(); // flush the currently open editor into the changes set

        // editing.changes is the synchronous source of truth (incl. the cell just closed); fall back to state.
        const live = grid ? grid.option("editing.changes") || [] : [];
        const editedRows = live.length ? live : changes;
        const items = editedRows
            .filter((c) => c && c.key && c.data)
            .map((c) => {
                const ordered =
                    c.data.Ordered != null && c.data.Ordered !== "" ? Number(c.data.Ordered) : null;
                return {
                    itemId: c.key, // ItemDto.RowId is the item GUID
                    WarehouseID: warehouseId || null,
                    lotId: null, // lots are auto-selected on the server
                    DetailType: "I",
                    quantityordered: ordered,
                    unitPrice: c.data.Price != null && c.data.Price !== "" ? Number(c.data.Price) : null,
                    DiscountPercent:
                        c.data.DiscPercent != null && c.data.DiscPercent !== "" ? Number(c.data.DiscPercent) : null,
                };
            })
            .filter((i) => i.quantityordered != null && i.quantityordered > 0);

        if (!items.length) {
            alert("Enter a positive Ordered quantity for at least one item.", "Select Items");
            return;
        }

        if (setLoadPanelVisible) setLoadPanelVisible(true);
        try {
            const res = await axios.post(
                `${window._env_.REACT_APP_APIS}/${routes.OM.InsertTransactionDetails}`,
                { HeaderId: headerRowId, Items: items },
                {
                    headers: {
                        "Content-Type": "application/json",
                        access_token: accessToken,
                        Authorization: `Bearer ${accessToken}`,
                    },
                }
            );
            if (res.data?.Success === false) {
                alert(res.data?.message || "Failed to add items.", "Error");
                return;
            }
            notify({ message: res.data?.message || "Items added to the order.", width: 300 }, "success", 3000);
            if (grid) grid.cancelEditData();
            if (onSuccess) onSuccess();
            handleHiding();
        } catch (err) {
            const msg = err.response?.data?.message || err.message;
            alert(typeof msg === "string" ? msg : JSON.stringify(msg), "Error");
        } finally {
            if (setLoadPanelVisible) setLoadPanelVisible(false);
        }
    }, [accessToken, headerRowId, warehouseId, onSuccess, handleHiding, setLoadPanelVisible, changes]);

    // Ordered / Price / Disc % / Disc Price / Amount are all independent editable cells — editing one does not
    // recalculate the others. The edited values live in the grid's batch-edit changes, so they survive
    // server-side paging and are read back on "Add Selected Items".
    const qtyFormat = { type: "fixedPoint", precision: 2 };
    const moneyFormat = { type: "currency", precision: 2, currency: "USD", useGrouping: true };

    if (!visible) return null;

    return (
        <Popup
            visible={true}
            onHiding={handleHiding}
            dragEnabled={true}
            hideOnOutsideClick={false}
            showCloseButton={true}
            showTitle={true}
            title={hasRange
                ? MESSAGES.SELECT_ITEMS_TITLE_WITH_RANGE
                    .replace("{0}", macroName || MESSAGES.MACRO_UNSAVED_RANGE)
                    .replace("{1}", rangeFilters.length)
                : MESSAGES.SELECT_ITEMS_TITLE}
            container="#root"
            width="90vw"
            height="90vh"
        >
            <div style={{ display: "flex", flexDirection: "column", height: "100%", minHeight: 0 }}>

                <DataGrid
                    ref={dataGridRef}
                    dataSource={resultData}
                    keyExpr="RowId"
                    remoteOperations={true}
                    showBorders={true}
                    hoverStateEnabled={true}
                    rowAlternationEnabled={true}
                    allowColumnReordering={true}
                    allowColumnResizing={true}
                    columnAutoWidth={true}
                    repaintChangesOnly={true}
                    height="calc(90vh - 190px)"
                    style={{ flex: "1 1 auto", minHeight: 0, marginTop: 8 }}
                    selectedRowKeys={selectedRowKeys}
                    onSelectionChanged={(e) => setSelectedRowKeys(e.selectedRowKeys)}
                >
                    <Selection mode="single" />
                    <Scrolling mode="standard" />
                    <LoadPanel enabled={true} />
                    <Sorting mode="multiple" />
                    <FilterRow visible={true} />
                    <Paging defaultPageSize={10} />
                    <Pager
                        visible={true}
                        showPageSizeSelector={true}
                        allowedPageSizes={allowedPageSizes}
                        showNavigationButtons={true}
                        showInfo={true}
                    />
                    <Editing
                        mode="batch"
                        allowUpdating={true}
                        useIcons={true}
                        selectTextOnEditStart={true}
                        changes={changes}
                        onChangesChange={setChanges}
                    />
                    <ColumnChooser enabled={true} title="Choose Column" width={290} height={290} sortOrder="asc">
                        <ColumnChooserSearch enabled={true} editorOptions={{ placeholder: "Search column" }} />
                    </ColumnChooser>

                    <Column dataField="RowId" caption="Row Id" visible={false} allowEditing={false} />
                    <Column dataField="WarehouseId" caption="WH" allowEditing={false} />
                    <Column dataField="ItemCode" caption="Item" allowEditing={false} />
                    <Column dataField="Description1" caption="Description 1 - Item" allowEditing={false} />
                    <Column
                        dataField="Available"
                        caption="Item - Avl"
                        dataType="number"
                        allowEditing={false}
                        format={qtyFormat}
                    />
                    <Column
                        dataField="NetAvailable"
                        caption="Item - Net Avl"
                        dataType="number"
                        allowEditing={false}
                        format={qtyFormat}
                    />
                    <Column
                        dataField="Ordered"
                        caption="Ordered"
                        dataType="number"
                        allowEditing={true}
                        allowFiltering={false}
                        allowSorting={false}
                        format={qtyFormat}
                    />
                    <Column
                        dataField="Price"
                        caption="Price"
                        dataType="number"
                        allowEditing={true}
                        allowFiltering={false}
                        allowSorting={false}
                        format={moneyFormat}
                    />
                    <Column
                        dataField="DiscPercent"
                        caption="Disc %"
                        dataType="number"
                        allowEditing={true}
                        allowFiltering={false}
                        allowSorting={false}
                        format={qtyFormat}
                    />
                    <Column
                        dataField="DiscPrice"
                        caption="Disc Price"
                        dataType="number"
                        allowEditing={true}
                        allowFiltering={false}
                        allowSorting={false}
                        format={moneyFormat}
                    />
                    <Column
                        dataField="Amount"
                        caption="Amount"
                        dataType="number"
                        allowEditing={false}
                        allowFiltering={false}
                        allowSorting={false}
                        format={moneyFormat}
                    />
                    <GridToolbar>
                        <GridToolbarItem location="after">
                            <Button
                                icon="trash"
                                disabled={!selectedRowKeys.length}
                                onClick={handleDeleteRows}
                            />
                        </GridToolbarItem>
                        <GridToolbarItem name="columnChooserButton" />
                    </GridToolbar>
                </DataGrid>

                <div
                    style={{
                        display: "flex",
                        justifyContent: "flex-end",
                        gap: 10,
                        paddingTop: 12,
                        borderTop: "1px solid #e0e0e0",
                        marginTop: 8,
                    }}
                >
                    <Button text="Add Selected Items" type="default" onClick={handleAddSelected} />
                    <Button text="Cancel" onClick={handleHiding} />
                </div>
            </div>
        </Popup>
    );
}

function mapStateToProps(state) {
    return {
        oidc: state.oidc,
    };
}

export default connect(mapStateToProps)(SelectItemsPopup);
